import { z } from "zod";
import { credentialPurposeSchema, type CredentialPurpose } from "./credentials";
import { canonicalIntegrationTypes, isRegisteredIntegrationProvider } from "./integrations";

export const toolHealthStates = ["healthy", "degraded", "unreachable", "unknown"] as const;
export const toolHealthSchema = z.enum(toolHealthStates);
export type ToolHealth = z.infer<typeof toolHealthSchema>;

export const toolAccesses = ["read", "write", "read-write"] as const;
export const toolAccessSchema = z.enum(toolAccesses);
export type ToolAccess = z.infer<typeof toolAccessSchema>;

export const toolRisks = ["read", "write", "destructive"] as const;
export const toolRiskSchema = z.enum(toolRisks);
export type ToolRisk = z.infer<typeof toolRiskSchema>;

export const toolBindingSchema = z.object({
  id: z.uuid(),
  projectId: z.uuid(),
  tool: z.string().min(1),
  integrationType: z.string().min(1).optional(),
  capabilities: z.array(z.string().min(1)),
  credentialRef: z.string().min(1),
  credentialValid: z.boolean(),
  credentialExpiresAt: z.iso.datetime().nullable(),
  /**
   * What the bound credential may do. The flow editor reads it to keep a step from binding a
   * write credential. Absent means the purpose was never recorded, which is treated as
   * read-only: an unknown purpose never widens what a step may hold.
   */
  credentialPurpose: credentialPurposeSchema.optional(),
  /**
   * Where the tool comes from: an integration connection, an MCP server, or an uploaded
   * OpenAPI spec. Absent means it was recorded before the source was tracked.
   */
  source: z.string().trim().min(1).max(200).optional(),
  /**
   * What the binding may do: read only, write only, or both. Reads follow the bound
   * credential purpose; an absent purpose is treated as read-only.
   */
  access: toolAccessSchema.optional(),
  /** Derived from the allowed operations: read, write, or destructive. Unknown means write. */
  risk: toolRiskSchema.optional(),
  /** Latest contract-test score (0-100), or null when no contract test has run yet. */
  contractTestScore: z.number().int().min(0).max(100).nullable().optional(),
  health: toolHealthSchema,
  usedByFlows: z.array(z.string().min(1)),
});
export type ToolBinding = z.infer<typeof toolBindingSchema>;

export const toolBindingListSchema = z.array(toolBindingSchema);
export type ToolBindingList = z.infer<typeof toolBindingListSchema>;

/**
 * A display-only label naming where a tool comes from: an integration connection, an MCP
 * server, or an uploaded OpenAPI spec. Never confers review or authority; the server replaces
 * it with the registered integration name when an integration type is given.
 */
export const toolSourceLabelSchema = z.string().trim().min(1).max(200);
export type ToolSourceLabel = z.infer<typeof toolSourceLabelSchema>;

/** The credential purposes a tool may be bound through. `model-key` is never valid for tools. */
export const toolCredentialPurposes = [
  "read-repository",
  "write-repository",
  "runtime-service",
] as const;
export const toolCredentialPurposeSchema = z.enum(toolCredentialPurposes);
export type ToolCredentialPurpose = z.infer<typeof toolCredentialPurposeSchema>;

/**
 * Derives the conservative binding access from the trusted credential purpose. Write-capable
 * purposes widen to the matching access: the caller cannot narrow them, so a write credential
 * can never be labelled read-only.
 */
export function deriveToolAccess(purpose: CredentialPurpose): ToolAccess {
  return purpose === "read-repository" ? "read" : "read-write";
}

/**
 * Derives the display source for a binding. A registered integration type always wins and is
 * named by its catalogue entry; otherwise the caller's display label is kept as untrusted
 * metadata; otherwise the source stays unrecorded.
 */
export function deriveToolSource(input: {
  integrationType?: string;
  source?: string;
}): string | undefined {
  if (input.integrationType) {
    const match = canonicalIntegrationTypes.find((entry) => entry.id === input.integrationType);
    if (match) return `Integration · ${match.name}`;
  }
  return input.source;
}

export const requestToolSchema = z
  .object({
    tool: z.string().trim().min(1).max(120),
    integrationType: z
      .string()
      .trim()
      .min(1)
      .max(120)
      .refine((val) => isRegisteredIntegrationProvider(val), {
        message: "Integration type must be a registered integration provider",
      })
      .optional(),
    capabilities: z.array(z.string().trim().min(1).max(120)).min(1),
    /**
     * The credential reference the binding is resolved against. The server looks it up in the
     * project's own credentials table and derives purpose, validity, expiry, access, and source
     * from that trusted row. An optional display label naming where the tool comes from may be
     * supplied, but it never affects authority and is replaced by the registered integration name
     * when an integration type is given. Access is never caller-supplied: the server derives it
     * from the trusted credential purpose, so a write credential cannot be labelled read-only.
     */
    credentialRef: z.string().trim().min(1).max(120),
    source: toolSourceLabelSchema.optional(),
  })
  .strict();
export type RequestTool = z.infer<typeof requestToolSchema>;

export const skillSources = ["marketplace", "uploaded"] as const;
export const skillSourceSchema = z.enum(skillSources);
export type SkillSource = z.infer<typeof skillSourceSchema>;

/**
 * A pinned content digest: `sha256:` followed by 64 hex characters. Catalogue rows pin
 * reviewed skill content with it, and installs pin the exact content they store with it.
 */
export const contentDigestSchema = z
  .string()
  .trim()
  .length(71)
  .refine((val) => /^sha256:[0-9a-f]{64}$/.test(val), {
    message: "Digest must pin the installed content (sha256: followed by 64 hex characters)",
  });
export type ContentDigest = z.infer<typeof contentDigestSchema>;

// Revoked is a withdrawal by the platform, not a state a project sets. It is in the enum so a
// revoked install can be reported rather than disappearing from the list.
export const skillStatuses = ["enabled", "trial", "private", "revoked"] as const;
export const skillStatusSchema = z.enum(skillStatuses);
export type SkillStatus = z.infer<typeof skillStatusSchema>;

export const skillUpdateStates = ["up-to-date", "update-available", "updating"] as const;
export const skillUpdateStateSchema = z.enum(skillUpdateStates);
export type SkillUpdateState = z.infer<typeof skillUpdateStateSchema>;

/**
 * Installs a skill into one project. The digest pins the exact content installed; an update
 * check never changes it. Source and status are never caller-supplied: the server decides
 * both after confirming the reviewed Marketplace path, and uploads stay private until an
 * approver confirms them.
 */
export const installSkillSchema = z
  .object({
    skill: z.string().trim().min(1).max(120),
    version: z.string().trim().min(1).max(80),
    digest: contentDigestSchema,
  })
  .strict();
export type InstallSkill = z.infer<typeof installSkillSchema>;

export const skillInstallSchema = z.object({
  id: z.uuid(),
  projectId: z.uuid(),
  skill: z.string().min(1),
  version: z.string().min(1),
  updateState: skillUpdateStateSchema,
  /**
   * Where the skill comes from: the Marketplace (a reviewed, signed pack) or an upload that
   * stays private to the project until an approver confirms it. Absent means it was recorded
   * before the source was tracked, which is treated as marketplace.
   */
  source: skillSourceSchema.optional(),
  /** Enabled, Trial, or Private. Absent means it was recorded before the status was tracked. */
  status: skillStatusSchema.optional(),
  /** Pinned content digest, set at install time and never changed by an update check. */
  digest: z.string().trim().min(1).max(200).optional(),
  /**
   * The permission list copied from the reviewed catalogue row at install time. Present
   * only on marketplace installs whose evaluation supplied a manifest permission list;
   * absent everywhere else, and never inferred from version, source, digest, or status.
   */
  permissions: z.array(z.string().min(1)).optional(),
  /** Latest eval score (0-100), or null when no eval has run yet. */
  evalScore: z.number().int().min(0).max(100).nullable().optional(),
  /** Names of the workflows that bind this skill. */
  usedIn: z.array(z.string().min(1)).optional(),
});
export type SkillInstall = z.infer<typeof skillInstallSchema>;

/**
 * `POST /api/v1/projects/:id/skills/:installId/approve`. Turns a private install into an enabled
 * one. The caller names the install and nothing else: the decision is about the row as it
 * stands, and a body carrying the skill or digest would let an approver confirm one thing while
 * a different row changes underneath.
 */
export const approveSkillInstallSchema = z
  .object({
    reason: z.string().trim().max(2000).optional(),
  })
  .strict();
export type ApproveSkillInstall = z.infer<typeof approveSkillInstallSchema>;

/** `POST /:id/skills/revocations/sync`. What the reconcile changed, and how many digests it read. */
export const syncRevocationsResultSchema = z.object({
  revoked: z.array(z.uuid()),
  checked: z.number().int().min(0),
});
export type SyncRevocationsResult = z.infer<typeof syncRevocationsResultSchema>;

export const skillInstallListSchema = z.array(skillInstallSchema);
export type SkillInstallList = z.infer<typeof skillInstallListSchema>;
